import { CookieStorage } from 'cookie-storage';
import React, { type FC, useEffect, useState } from 'react';
import {
	ONETRUST_CLOSED,
	ONETRUST_PREFERENCES,
} from '../../../constants/cookies';
import { gtmId } from '../../../settings/production';
import { useAppSelector } from '../../redux/hooks';

interface GtmSnippetProps {
	noGtm?: boolean;
}

export const GtmSnippet: FC<GtmSnippetProps> = ({ noGtm = false }) => {
	const [registered, setRegistered] = useState(false);
	const [canRenderGtm, setCanRenderGtm] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	const reduxNoGtm = useAppSelector((state) => state.config.noGtm);
	const isGtmDisabled = noGtm || reduxNoGtm;

	// Server and first client render both return null, so hydration matches before
	// the consent manager (window-only) is allowed to decide anything.
	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (!isMounted) return;

		const { UnionConsentManagement: consentManager } = window;
		const cookieStorage = new CookieStorage();

		if (isGtmDisabled || registered || typeof consentManager === 'undefined') {
			return;
		}

		if (
			cookieStorage.getItem(ONETRUST_PREFERENCES) ||
			cookieStorage.getItem(ONETRUST_CLOSED)
		) {
			setCanRenderGtm(true);
			setRegistered(true);
			return;
		}

		// Hide OneTrust policy text during LCP critical window
		const hideStyle = document.createElement('style');
		hideStyle.textContent =
			'#onetrust-policy-text { visibility: hidden !important; }';
		document.head.appendChild(hideStyle);

		// Show it back when browser is idle
		requestIdleCallback(() => hideStyle.remove());

		consentManager.registerService({
			name: 'GTM',
			setup: (_) => {
				// This callback is called when the user takes an action (approve or reject)
				// In this context, we don't care about the consent values.
				// We just need to know that UnionConsentManagement is ready.
				// Once the GTM snippet is injected, it will respect the consent values.
				setCanRenderGtm(true);
			},
		});

		// This prevents the service from being registered multiple times.
		setRegistered(true);
	}, [isGtmDisabled, registered, isMounted]);

	if (!isMounted || isGtmDisabled || !canRenderGtm) {
		return null;
	}

	return (
		<script
			data-testid="gtm-script"
			type="text/javascript"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: necessary
			dangerouslySetInnerHTML={{
				__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','${gtmId}');`,
			}}
		/>
	);
};
